import { useQuery } from '@tanstack/react-query';
import { SessionEndedError, type Profile } from '@cerebro-diem/core';
import { getSession, supabase } from './supabase';
import { queryKeys } from './queries';

/** The signed-in user's profile (threshold, time zone, GTD contexts). */
export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const { data, error } = await supabase.from('profiles').select('*').single();
      if (error) throw error;
      return data as Profile;
    },
  });
}

/** The profile settings a user may change (the database refuses any other column). */
export type ProfileUpdate = Partial<
  Pick<Profile, 'display_name' | 'digest_time' | 'weekly_review_day' | 'weekly_review_time' | 'timezone' | 'confidence_threshold' | 'gtd_contexts'>
>;

/** Save settings on the signed-in user's profile. Throws unless the row was updated. */
export async function updateProfile(userId: string | undefined, update: ProfileUpdate, signal?: AbortSignal): Promise<Profile> {
  if (!userId) throw new Error('you are signed out; sign in and try again');
  let request = supabase.from('profiles').update(update).eq('id', userId).select('*');
  if (signal) request = request.abortSignal(signal);
  const { data, error } = await request.maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('your profile was not found');
  return data as Profile;
}

/**
 * Give the signed-in user's profile this browser's time zone when it has none
 * yet (set_initial_time_zone); a zone already set is kept.
 */
export async function setInitialTimeZone(zone: string): Promise<void> {
  const { error } = await supabase.rpc('set_initial_time_zone', { p_zone: zone });
  if (error) throw error;
}

/** The time zones a profile may name: the database's own list. */
export function useTimeZones() {
  return useQuery({
    queryKey: ['time-zones'],
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('time_zone_names');
      if (error) throw error;
      if (!Array.isArray(data) || data.some((zone) => typeof zone !== 'string')) {
        throw new Error('the list of time zones did not arrive');
      }
      return data as string[];
    },
  });
}

/**
 * Save the GTD context list and give the user's tasks the new name of each
 * renamed context, in one database call (save_gtd_contexts): all of it or
 * none. Two names can swap. Returns how many tasks were renamed.
 */
export async function saveGtdContexts(contexts: readonly string[], renames: ReadonlyMap<string, string>, signal?: AbortSignal, expectedUserId?: string): Promise<number> {
  if (!expectedUserId) throw new SessionEndedError('Sign in before saving contexts');
  const { data: { session } } = await getSession();
  if (!session) throw new SessionEndedError('No active session remains');
  if (session.user.id !== expectedUserId) throw new Error('The account changed; these contexts were not sent');
  if (signal?.aborted) throw new Error('Saving was stopped before the request was sent');
  let request = supabase.rpc('save_gtd_contexts', { p_contexts: contexts, p_renames: Object.fromEntries(renames) })
    .setHeader('Authorization', `Bearer ${session.access_token}`);
  if (signal) request = request.abortSignal(signal);
  const { data, error } = await request;
  if (error) throw error;
  const renamed: unknown = data && typeof data === 'object' ? (data as Record<string, unknown>).renamed : undefined;
  if (typeof renamed !== 'number') throw new Error('saving the contexts did not say what changed');
  return renamed;
}
